import { CreatorApiError, requests } from './creatorApiAgent';
import { PAYMENT_REQUIRED_STATUS, creditsRefusalReasonOf } from '../../utils/notEnoughCredits';
import { IMAGE_CONTENT_TYPE_KEY, TGenerateImageOutcome, TGenerateImageRequest, TGenerateImageResponse, TGenerationAllowance, TMediaStyle } from '../models/AiMediaModels';

/**
 * The media generation endpoints, grouped under the backend's `AiMedia` controller. Request and
 * envelope plumbing lives in `creatorApiAgent`.
 *
 * Knows nothing about SignalR, the dialog or React. It owns one piece of endpoint semantics the
 * store should not have to know: which HTTP refusals mean "that slot is already generating" and
 * "not enough credits".
 */

/** The backend answers 409 when the target content already has a generation in flight. */
const HTTP_CONFLICT = 409;

const SERVICE_UNREACHABLE_MESSAGE = 'The image generation service could not be reached. Please try again.';

/**
 * Everything this agent rejects with is fit to put in front of an author. A refusal the backend
 * wrote arrives as a `CreatorApiError` and is passed on untouched — its prose is the whole point. A
 * transport that never reached an envelope has only its own words, which say nothing an author can
 * act on, so they go to the log and ours go to the screen.
 */
const asAuthorFacing = (error: unknown): never => {
  if (error instanceof CreatorApiError) throw error;

  console.warn('The media generation request failed before the backend answered', error);
  throw new Error(SERVICE_UNREACHABLE_MESSAGE);
};

const AiMedia = {
  /**
   * The styles on offer for image contents, in the order they should be offered. An owner with no
   * active styles is answered with an empty list, not a failure.
   */
  getMediaStyles: (): Promise<TMediaStyle[]> =>
    requests
      .postEnvelope<TMediaStyle[]>('/AiMedia/GetMediaStyles', { contentTypeKey: IMAGE_CONTENT_TYPE_KEY })
      .then((styles) => styles ?? [])
      .catch(asAuthorFacing),

  /**
   * How many generations the author has on the way, and how many they may have at once. Null when
   * the backend answered without one, which says as little as a failure does.
   */
  getGenerationAllowance: (): Promise<TGenerationAllowance | null> => requests.postEnvelope<TGenerationAllowance>('/AiMedia/GetGenerationAllowance').catch(asAuthorFacing),

  /**
   * Submits a generation for one placeholder content. Resolves for the two answers that say
   * something definite about the slot, and rejects for every refusal the author has to read.
   */
  generateImage: (request: TGenerateImageRequest): Promise<TGenerateImageOutcome> =>
    requests
      .postEnvelope<TGenerateImageResponse>('/AiMedia/GenerateImage', request)
      .then((response): TGenerateImageOutcome => ({ kind: 'accepted', balance: response?.balance ?? null }))
      .catch((error: unknown): TGenerateImageOutcome => {
        if (error instanceof CreatorApiError && error.status === HTTP_CONFLICT) return { kind: 'alreadyGenerating' };
        // The backend answers 402 when the author's credits do not cover the image, names a debt on
        // purchased credits beyond the limit apart by its reactName, and carries the balance as it stands.
        if (error instanceof CreatorApiError && error.status === PAYMENT_REQUIRED_STATUS) {
          return { kind: 'notEnoughCredits', reason: creditsRefusalReasonOf(error.reactName), balance: (error.data as TGenerateImageResponse | null)?.balance ?? null };
        }
        return asAuthorFacing(error);
      }),
};

const aiMediaAgent = {
  AiMedia,
};

export default aiMediaAgent;
